import { Box, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { Card } from '@mui/material';
import StatisticResource, { Aggregation, Statistic } from '../../resources/StatisticResource';
import LineChart from '../../design/charts/LineChart';
import { DateRange } from '../../design';
import { plColors } from '../..';
import { useEffect, useState } from 'react';
import { Requester } from '../../hooks/useRequester';
import moment from 'moment';

interface Datapoint {
  x: number;
  y: number | null;
}

interface Config {
  toDatapoint: (value: StatisticResource) => Datapoint;
  toAverage: (values: StatisticResource[]) => number | string;
  fetcher: Requester;
  margin: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
  title: string;
  statistic: Statistic;
  userId?: string;
}

interface Props {
  configs: Config[];
  userId?: string;
  eikenTestType?: string;
}

export default function LineChartWithAPI({ configs, userId, eikenTestType }: Props) {
  const [aggregation, setAggregation] = useState<Aggregation>(Aggregation.Week);
  const [to, setTo] = useState<Date>(new Date());
  const [testType, setTestType] = useState<string>('*');

  const from =
    aggregation === Aggregation.Week
      ? moment(to).subtract(12, 'weeks').toDate()
      : moment(to).subtract(12, 'months').toDate();

  const type = eikenTestType ?? testType;

  useEffect(() => {
    const query: any = {
      aggregation,
      from,
      to,
      userId,
    };

    configs.forEach((config) => {
      // We don't show study time for each test type yet
      if (config.statistic !== Statistic.EikenStudyTimes && type !== '*') {
        query.eikenTestType = type;
      }

      config.fetcher.submit(query);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregation, to, type]);

  return (
    <Card sx={{ pl: 1 }}>
      {configs.length !== 1 && (
        <Box className="ml-6 flex items-center gap-3 mb-6">
          <Typography variant="body2" color="text.secondary">
            View by
          </Typography>
          <ToggleButtonGroup
            color="primary"
            value={aggregation}
            exclusive
            onChange={(event, value) => setAggregation(value)}
            aria-label="Platform"
          >
            <ToggleButton value={Aggregation.Week}>Week</ToggleButton>
            <ToggleButton value={Aggregation.Month}>Month</ToggleButton>
          </ToggleButtonGroup>
          <Select
            size="small"
            value={testType}
            onChange={(e) => {
              setTestType(e.target.value);
            }}
          >
            <MenuItem value="*">Any</MenuItem>
            <MenuItem value="fulltest">Full Test</MenuItem>
            <MenuItem value="pronunciation">Pronunciation</MenuItem>
            <MenuItem value="q1">Question 1</MenuItem>
            <MenuItem value="q2">Question 2</MenuItem>
            <MenuItem value="q3">Question 3</MenuItem>
            <MenuItem value="q4">Question 4</MenuItem>
          </Select>
          <Box className="flex-grow"></Box>
          <Box sx={{ mr: 2 }}>
            <DateRange to={to} aggregation={aggregation} onChange={setTo} />
          </Box>
        </Box>
      )}
      <Box className={`grid grid-cols-${configs.length > 1 ? 2 : 1} gap-10`}>
        {configs.map((config) => {
          const { toDatapoint, toAverage, fetcher, margin, title } = config;

          const values = StatisticResource.listStatistic(
            from,
            to,
            config.statistic,
            aggregation,
            userId,
            // We don't show study time for each test type yet
            config.statistic !== Statistic.EikenStudyTimes ? type : '*'
          );

          const datapoints = values.map(toDatapoint);
          const average = toAverage(values);

          const xAxisFormatter = (v: any, context: any) => {
            if (context.location === 'tick') {
              return aggregation === Aggregation.Month
                ? moment(v).format("MMM 'YY")
                : moment(v).format('MMM D');
            }

            return aggregation === Aggregation.Month
              ? moment(v).format('MMM YYYY')
              : moment(v).format('[Week of] MMM D, YYYY');
          };

          return (
            <Box
              key={config.statistic}
              className={`flex flex-col ${configs.length === 1 ? 'gap-5' : ''}`}
            >
              <Box className="flex flex-row justify-between items-center" sx={{ ml: 3, mr: 2 }}>
                <Typography variant="h3">{title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Average: {average}
                </Typography>
              </Box>

              {configs.length === 1 && (
                <Box className="ml-6 flex items-center gap-3">
                  <Typography variant="body2" color="text.secondary">
                    View by
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    value={aggregation}
                    exclusive
                    onChange={(event, value) => setAggregation(value)}
                  >
                    <ToggleButton value={Aggregation.Week}>Week</ToggleButton>
                    <ToggleButton value={Aggregation.Month}>Month</ToggleButton>
                  </ToggleButtonGroup>
                  <Box className="flex-grow"></Box>
                  <Box sx={{ mr: 2 }}>
                    <DateRange to={to} aggregation={aggregation} onChange={setTo} />
                  </Box>
                </Box>
              )}

              <LineChart
                loading={fetcher.loading}
                dataset={datapoints.map((item) => ({
                  x: new Date(item.x).getTime(),
                  y: item.y,
                }))}
                series={[{ color: plColors.primary[2] }]}
                xAxisFormatter={xAxisFormatter}
                margin={margin}
              />
            </Box>
          );
        })}
      </Box>
    </Card>
  );
}
